import { computed, toValue } from "vue";

/**
 * @composable usePageWindow
 * @description Computes the window of pages to display in a paginator, with ellipsis for skipped ranges.
 * Handles refs, reactive properties, and plain values transparently via `toValue` (ref, getter o numero).
 * @param {import('vue').Ref<number> | number | () => number} totalPage - Total number of pages (ref, getter, or number)
 * @param {import('vue').Ref<number> | number | () => number} currentPage - Current page (ref, getter, or number)
 * @param {Object} options - Configuration options
 * @param {number} [options.siblings=1] - Number of pages to show before/after current page
 * @returns {{
 *   items: import('vue').ComputedRef<Array<{
 *     type: 'page' | 'ellipsis',
 *     page?: number,
 *     current?: boolean,
 *     edge?: boolean,
 *     key: string
 *   }>>
 * }}
 */
export function usePageWindow(totalPage, currentPage, { siblings = 1 } = {}) {
  const items = computed(() => {
    const total = toValue(totalPage);
    const current = toValue(currentPage);

    // If total pages fit within window, show all
    if (total <= 5 + 2 * siblings) {
      return Array.from({ length: total }, (_, i) => ({
        type: "page",
        page: i + 1,
        current: i + 1 === current,
        edge: i + 1 === 1 || i + 1 === total,
        key: `page-${i + 1}`,
      }));
    }

    // Build window: [1] [current-siblings...current+siblings] [total]
    const result = [];
    const pages = new Set();

    // Always include first and last pages
    pages.add(1);
    pages.add(total);

    // Include current page and siblings
    for (let i = Math.max(1, current - siblings); i <= Math.min(total, current + siblings); i++) {
      pages.add(i);
    }

    // Convert to sorted array and build result
    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    let lastPage = 0;

    for (const page of sortedPages) {
      // Add ellipsis if there's a gap
      if (page > lastPage + 1) {
        result.push({
          type: "ellipsis",
          key: `ellipsis-${lastPage}-${page}`,
        });
      }

      // Add page button
      result.push({
        type: "page",
        page,
        current: page === current,
        edge: page === 1 || page === total,
        key: `page-${page}`,
      });

      lastPage = page;
    }

    return result;
  });

  return { items };
}
